import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { MELT_PRICING_FIXTURE } from '@/lib/fixtures/catalog';
import { useCustomer } from './useCustomer';
import type { CatalogItem } from '@/types/domain';

/**
 * useCustomerPricing — returns a Map<catalogItemId, unitPrice> for the
 * authenticated customer. Falls back to the Melt $68-3-gal pricing fixture
 * when the schema isn't pushed yet or when no customer_pricing rows exist
 * for the logged-in user (pre-seed state).
 */
export function useCustomerPricing() {
  const { data: ctx } = useCustomer();

  return useQuery<Map<string, number>>({
    queryKey: ['customer-pricing', ctx?.customer.id ?? 'guest'],
    queryFn: async (): Promise<Map<string, number>> => {
      const fixture = (): Map<string, number> => {
        const m = new Map<string, number>();
        // Pilot fallback: assume The Melt pricing if we have no customer context.
        if (!ctx || ctx.customer.qboCustomerId === 1418) {
          Object.entries(MELT_PRICING_FIXTURE).forEach(([id, price]) =>
            m.set(id, price),
          );
        }
        return m;
      };

      if (!ctx) return fixture();

      // Pull resolved prices from the central Pricing Control Center via the
      // server (contract → BX-1 standard → list). Server-scoped to this
      // customer so contract pricing never leaks. Falls back to the fixture on
      // any error / empty result (pre-seed state).
      try {
        const { data: session } = await supabase.auth.getSession();
        const jwt = session.session?.access_token;
        if (!jwt) return fixture();
        const res = await fetch(
          `/.netlify/functions/customer-prices?customer_id=${encodeURIComponent(ctx.customer.id)}`,
          { headers: { Authorization: `Bearer ${jwt}` } },
        );
        if (!res.ok) return fixture();
        const body = (await res.json()) as { ok: boolean; prices?: Record<string, number> };
        if (!body.ok || !body.prices) return fixture();
        const out = new Map<string, number>();
        for (const [id, price] of Object.entries(body.prices)) out.set(id, Number(price));
        return out.size > 0 ? out : fixture();
      } catch {
        return fixture();
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * True while the pricing query hasn't resolved yet. The query always returns
 * a Map once settled (the fixture path included), so `undefined` means
 * "still loading" — surfaces should show a placeholder instead of falling
 * back to the catalog list price, which can differ from the customer's
 * resolved price and reads as a price flicker ($32 → $28).
 */
export function pricingPending(pricing: Map<string, number> | undefined): boolean {
  return pricing === undefined;
}

/** Resolve unit price for an item using the customer-pricing map (with list fallback). */
export function priceFor(
  item: CatalogItem,
  pricing: Map<string, number> | undefined,
): { unitPrice: number; isCustom: boolean } {
  const custom = pricing?.get(item.id);
  if (custom !== undefined) return { unitPrice: custom, isCustom: true };
  return { unitPrice: item.listPrice, isCustom: false };
}
